import { Link } from 'react-router-dom';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { flat, listOf } from '../entity/entity';
import { faceLine } from '../plan/arc';
import type { Arc } from '../plan/arc';
import { arcHref } from '../routes/ArcRoute';
import { ArchivedGlyph, StatusGlyph, StorylineGlyph } from './StatusGlyph';
import { FolderLink, useFolders } from '../folders/FolderChip';

/**
 * One realisation of an arc, used identically in a status band, the queue, a
 * storyline's member list and a tinted card. **The row never changes between
 * them — the holder around it does**, and no holder may add horizontal inset,
 * so a nested arc wraps its text exactly as a standalone one does.
 *
 * Lanes, left to right: grip 16 · status 20 · content flex · kebab 26. Both
 * hover-revealed lanes are ALWAYS reserved, so revealing them never shifts
 * text and never covers a word.
 */

export type Face = 1 | 2 | 3;

export interface ArcRowProps {
  arc: Arc;
  face: Face;
  compact?: boolean;
  /** Depth ≥ 1 renders a path crumb instead of stepping right — there is no
   *  indentation ladder and no disclosure column. */
  crumb?: string;
  dragging?: boolean;
  dropBefore?: boolean;
  movable?: boolean;
  onPointerDown?: (event: ReactPointerEvent) => void;
  /** Carries the glyph's rect, because the panel it opens is rendered as a
   *  sibling of the whole board and has no other way to find this row. */
  onOpenPicker?: (anchor: DOMRect) => void;
}

export function ArcRow({
  arc,
  face,
  compact = false,
  crumb,
  dragging = false,
  dropBefore = false,
  movable = false,
  onPointerDown,
  onOpenPicker,
}: ArcRowProps) {
  const inside = listOf(arc.frontMatter['arcs']).length;
  const line = faceLine(arc);
  const done = arc.status === 'done';

  return (
    <article
      className={
        `arcrow${compact ? ' compact' : ''}${dragging ? ' dragging' : ''}` +
        `${dropBefore ? ' drop-before' : ''}${done ? ' is-done' : ''}`
      }
      data-arc-name={movable ? arc.name : undefined}
      onPointerDown={movable ? onPointerDown : undefined}
    >
      <span className="lane-grip" aria-hidden="true">
        <GripDots />
      </span>
      <span className="lane-status">
        {onOpenPicker ? (
          <button
            type="button"
            className="status-button"
            title={
              arc.archived
                ? `archived — still ${arc.status} in its file`
                : `${arc.status} — change`
            }
            // The press must not also start a drag: the glyph is a control,
            // and the row around it is a handle.
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => onOpenPicker(e.currentTarget.getBoundingClientRect())}
          >
            {arc.archived ? <ArchivedGlyph /> : <StatusGlyph status={arc.status} />}
          </button>
        ) : arc.archived ? (
          <span title={`archived — still ${arc.status} in its file`}>
            <ArchivedGlyph />
          </span>
        ) : (
          <StatusGlyph status={arc.status} />
        )}
      </span>

      <div className="lane-content">
        {crumb && <div className="arcrow-crumb">{crumb} ›</div>}
        <div className="arcrow-title">
          <h3 className="t-entityTitle">
            {inside > 0 && <StorylineGlyph />}
            <Link to={arcHref(arc.name)}>{arc.title}</Link>
          </h3>
          <span className="arcrow-pins t-metaMono">
            {inside > 0 && <span>{inside} inside</span>}
            {arc.frontMatter['updated'] && (
              <span className="arcrow-updated">{flat(arc.frontMatter['updated'])}</span>
            )}
          </span>
        </div>

        {/* The ask ignores the face level. Faces exist to decide how much
            DESCRIPTION a row can afford; a question waiting on the reader is
            not description, and a compact row that hides it is the exact
            failure the state was added to fix. */}
        {(face >= 2 || line.asking) && line.text && (
          <p className={`arcrow-prose t-faceProse${line.asking ? ' is-asking' : ''}`}>
            {line.starting && <span className="t-eyebrowMono prose-label">start with</span>}
            {line.text}
          </p>
        )}
        {face >= 3 && <MetaRun arc={arc} />}
      </div>

      <span className="lane-kebab" aria-hidden="true">
        ⋯
      </span>
    </article>
  );
}

function MetaRun({ arc }: { arc: Arc }) {
  const folders = useFolders();
  const tags = listOf(arc.frontMatter['tags']);
  const markers = listOf(arc.frontMatter['markers']);
  const area = flat(arc.frontMatter['area']);
  const sources = listOf(arc.frontMatter['sources']).length;
  if (!tags.length && !markers.length && !area && !sources) return null;

  return (
    <div className="arcrow-meta t-metaMono">
      {tags.map((tag) => (
        <FolderLink key={tag} name={tag} folders={folders} className="meta-tag">
          {tag}
        </FolderLink>
      ))}
      {markers.map((marker) => (
        <span key={marker} className="meta-marker">
          ⌇ {marker}
        </span>
      ))}
      {area && <span>{area}</span>}
      {sources > 0 && <span>{sources} sources</span>}
    </div>
  );
}

export function GripDots() {
  return (
    <svg width="7" height="13" viewBox="0 0 7 13" className="grip">
      {[2, 6.5, 11].map((y) => (
        <g key={y}>
          <circle cx="1.6" cy={y} r="1" fill="currentColor" />
          <circle cx="5.4" cy={y} r="1" fill="currentColor" />
        </g>
      ))}
    </svg>
  );
}
